import { Gender } from "@/types/common";
import { isFuture } from "date-fns";

export interface PeselData {
  personalIdNumber: string;
  birthDate?: string;
  gender?: Gender;
}

export const PESEL_ERRORS = {
  EMPTY_PESEL: "empty_pesel",
  INVALID_FORMAT: "invalid_format",
  INVALID_BIRTH_DATE: "invalid_birth_date",
  INVALID_GENDER: "invalid_gender",
  CHECKSUM_FAILED: "checksum_failed",
  BIRTH_DATE_MISMATCH: "birth_date_mismatch",
  GENDER_MISMATCH: "gender_mismatch",
  BIRTH_DATE_IN_FUTURE: "birth_date_in_future",
} as const;

export type PeselError = (typeof PESEL_ERRORS)[keyof typeof PESEL_ERRORS];

/**
 * PESEL weights configuration
 * Based on official: https://www.gov.pl/web/gov/czym-jest-numer-pesel
 */
const PESEL_CHECKSUM_WEIGHTS = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3] as const;

/**
 * PESEL century mapping configuration
 * Based on official: https://www.gov.pl/web/gov/czym-jest-numer-pesel
 */
const CENTURY_MAPPINGS: {
  min: number;
  max: number;
  offset: number;
  century: number;
}[] = [
  { min: 81, max: 92, offset: 80, century: 1800 },
  { min: 61, max: 72, offset: 60, century: 2200 },
  { min: 41, max: 52, offset: 40, century: 2100 },
  { min: 21, max: 32, offset: 20, century: 2000 },
  { min: 1, max: 12, offset: 0, century: 1900 },
] as const;

/**
 * Validates PESEL format (11 digits)
 */
export function validatePeselFormat(pesel: string): boolean {
  return /^\d{11}$/.test(pesel);
}

/**
 * Validates if a date is valid
 */
function isValidDate(year: number, month: number, day: number): boolean {
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

/**
 * Formats a date as YYYY-MM-DD
 */
function formatDate(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * Extracts birth date from PESEL number
 */
export function extractBirthDateFromPesel(pesel: string): string | null {
  if (!validatePeselFormat(pesel)) return null;

  const yearPart = Number(pesel.substring(0, 2));
  const monthPart = Number(pesel.substring(2, 4));
  const dayPart = Number(pesel.substring(4, 6));

  const mapping = CENTURY_MAPPINGS.find(
    ({ min, max }) => monthPart >= min && monthPart <= max,
  );
  if (!mapping) return null;

  const month = monthPart - mapping.offset;
  const year = mapping.century + yearPart;

  if (!isValidDate(year, month, dayPart)) return null;

  return formatDate(year, month, dayPart);
}

/**
 * Extracts gender from PESEL number
 * 10th digit (index 9) determines gender
 * Odd numbers are male, even numbers are female
 */
export function extractGenderFromPesel(pesel: string): Gender | null {
  if (!validatePeselFormat(pesel)) return null;
  const genderDigit = parseInt(pesel[9]);
  return genderDigit % 2 === 1 ? "male" : "female";
}

/**
 * Validates PESEL checksum
 * Based on official: https://www.gov.pl/web/gov/czym-jest-numer-pesel
 */
export function validatePeselChecksum(pesel: string): boolean {
  if (!validatePeselFormat(pesel)) return false;

  const sum = PESEL_CHECKSUM_WEIGHTS.reduce(
    (acc, w, i) => acc + Number(pesel[i]) * w,
    0,
  );

  const checksum = (10 - (sum % 10)) % 10;
  return checksum === parseInt(pesel[10]);
}

/**
 * Main PESEL validation function
 * Returns error code string or null if valid
 */
export function validatePeselData(data: PeselData): PeselError | null {
  const { personalIdNumber, birthDate, gender } = data;

  // Empty PESEL validation
  if (!personalIdNumber || personalIdNumber.trim() === "") {
    return PESEL_ERRORS.EMPTY_PESEL;
  }

  // Format validation
  if (!validatePeselFormat(personalIdNumber)) {
    return PESEL_ERRORS.INVALID_FORMAT;
  }

  // Extract data from PESEL first to validate date
  const peselBirthDate = extractBirthDateFromPesel(personalIdNumber);
  if (!peselBirthDate) {
    return PESEL_ERRORS.INVALID_BIRTH_DATE;
  }

  // Check if birth date is in the future
  if (isFuture(new Date(peselBirthDate))) {
    return PESEL_ERRORS.BIRTH_DATE_IN_FUTURE;
  }

  // Gender validation
  const peselGender = extractGenderFromPesel(personalIdNumber);
  if (!peselGender) {
    return PESEL_ERRORS.INVALID_GENDER;
  }

  // Checksum validation (after date validation to avoid false positives)
  if (!validatePeselChecksum(personalIdNumber)) {
    return PESEL_ERRORS.CHECKSUM_FAILED;
  }

  // Birth date validation
  if (birthDate && peselBirthDate !== birthDate) {
    return PESEL_ERRORS.BIRTH_DATE_MISMATCH;
  }

  // Gender validation
  if (gender && peselGender !== gender) {
    return PESEL_ERRORS.GENDER_MISMATCH;
  }

  return null; // Valid
}
